import React, { useState } from 'react';

import appConfig from '@sdk/config/appConfig';
import { useFeatureFlag } from '@sdk/featureFlags/useFeatureFlag';
import { FeatureFlag } from '@sdk/featureFlags/types';
import useSlingSession from '@sdk/login/hooks/useSlingSession';

import { ConfirmationScreen } from './ConfirmationScreen';
import LegacyWrapper from '../components/LegacyWrapper';
import useToastInstall from './ToastInstall.hooks';
import { LoginOrRegister } from './LoginOrRegister';
import SelectOrgConnect from './SelectOrgConnect';

export function ToastInstall() {
  const queryParams = new URLSearchParams(window.location.search);

  const { currentUser, error, configureToast, signIn, signUp, cancel } = useToastInstall();
  const session = useSlingSession();
  const usingOnboardingSessionToken = queryParams.has('onboardingSessionToken');

  const isOrgSelectorEnabled = useFeatureFlag(FeatureFlag.TOAST_ONBOARDING_ORG_SELECTOR_ENABLED);
  // isOrgSelected represents whether an org has been chosen to connect to. If you created a new account, this is treated as having already selected an org.
  const [isOrgSelected, setIsOrgSelected] = useState(
    queryParams.get('authorized') === 'new_account'
  );
  // pastLoginStage is true when the current user has logged in or has chosen to connect with the existing logged in account, and false otherwise.
  // The presence of the authorized parameter shows the user has been redirected to the login or register page.
  const [pastLoginStage, setPastLoginStage] = useState(queryParams.has('authorized'));

  const connectToToast = () => {
    configureToast();
  };

  const onSignInClick = () => {
    if (!currentUser) {
      signIn();
      return;
    }

    setPastLoginStage(true);

    const showOrgSelector = currentUser.orgs.length > 1 && isOrgSelectorEnabled;
    if (!showOrgSelector) {
      connectToToast();
      return;
    }
  };

  const onSignUpClick = () => {
    signUp();
  };

  const handleOrgSelect = async (orgId: number) => {
    await session.init(orgId);
    setIsOrgSelected(true);
  };

  const cancelOrgSelect = () => (window.location.href = appConfig.TOAST_CONNECT_URL);

  if (error) {
    return <LegacyWrapper>{error}</LegacyWrapper>;
  }

  // New onboarding flow
  if (usingOnboardingSessionToken) {
    if (currentUser && pastLoginStage) {
      if (currentUser.orgs.length > 1 && isOrgSelectorEnabled && !isOrgSelected) {
        return (
          <SelectOrgConnect
            orgs={currentUser.orgs}
            onSelect={handleOrgSelect}
            onCancel={cancelOrgSelect}
          />
        );
      }

      connectToToast();
      return null;
    }

    // Prompt user to log-in to or create the account they'd like to link to toast
    // Note: shown once even if they're logged in, so that users can choose to link to a different sling account
    // than they are currently logged in to
    return <LoginOrRegister onSignIn={onSignInClick} onSignUp={onSignUpClick} />;
  }

  // Old onboarding flow, to be cleaned up once multi org onboarding is rolled out to 100%
  if (currentUser?.orgs.length > 1 && isOrgSelectorEnabled && !isOrgSelected) {
    return (
      <SelectOrgConnect
        orgs={currentUser.orgs}
        onSelect={handleOrgSelect}
        onCancel={cancelOrgSelect}
      />
    );
  }

  if (currentUser) {
    return <ConfirmationScreen onConfirm={configureToast} onCancel={cancel} />;
  }

  if (queryParams.has('isFirstConnection')) {
    return <LoginOrRegister onSignIn={signIn} onSignUp={signUp} />;
  }

  signIn();
  return null;
}
